import { useEffect, useState } from 'react';
import type { Collection, Photo } from '../types';
import { HelpTooltip } from '../components/HelpTooltip';
import { api } from '../api';
import './InfoPanel.css';

interface Props {
  photo: Photo;
  collections: Collection[];
  onChanged: () => void;
  onOpen: () => void;
  onDelete: () => void;
}

export function InfoPanel({
  photo,
  collections,
  onChanged,
  onOpen,
  onDelete,
}: Props) {
  const [keywords, setKeywords] = useState(photo.keywords.join(', '));
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    setKeywords(photo.keywords.join(', '));
    setStatus(null);
  }, [photo.id, photo.keywords]);

  async function saveKeywords() {
    const list = keywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
    await api.setKeywords(photo.id, list);
    onChanged();
  }

  async function addTo(collectionId: string) {
    const name =
      collections.find((c) => c.id === collectionId)?.name ?? 'collection';
    setStatus(`Adding to ${name}…`);
    try {
      await api.addToCollection(collectionId, photo.id);
      setStatus(`Added to ${name}`);
      onChanged();
    } catch (e) {
      setStatus(
        e instanceof Error ? `Failed: ${e.message}` : 'Could not add photo',
      );
    }
  }

  return (
    <aside className="info">
      <img className="info__preview" src={photo.thumbUrl} alt={photo.filename} />
      <button className="chip chip--primary info__edit" onClick={onOpen}>
        Open in Editor
      </button>

      <div className="info__section">
        <h4 className="info__h">
          Keywords <HelpTooltip id="keywords" />
        </h4>
        <textarea
          className="info__kw"
          value={keywords}
          placeholder="beach, sunset, family"
          onChange={(e) => setKeywords(e.target.value)}
          onBlur={saveKeywords}
        />
      </div>

      <div className="info__section">
        <h4 className="info__h">
          Collections <HelpTooltip id="collections" />
        </h4>
        <select
          className="info__select"
          value=""
          onChange={(e) => e.target.value && addTo(e.target.value)}
        >
          <option value="">Add to collection…</option>
          {collections.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {collections.length === 0 && (
          <p className="info__note">Create a collection first.</p>
        )}
        {status && <p className="info__note">{status}</p>}
      </div>

      <div className="info__section">
        <h4 className="info__h">Metadata</h4>
        <dl className="info__meta">
          <dt>File</dt>
          <dd>{photo.filename}</dd>
          <dt>Size</dt>
          <dd>
            {photo.width} × {photo.height}
          </dd>
          <dt>Weight</dt>
          <dd>{(photo.sizeBytes / 1024 / 1024).toFixed(1)} MB</dd>
          {photo.camera && (
            <>
              <dt>Camera</dt>
              <dd>{photo.camera}</dd>
            </>
          )}
        </dl>
      </div>

      <button className="chip info__delete" onClick={onDelete}>
        Delete photo
      </button>
    </aside>
  );
}
